'use client'

import Link from 'next/link'
import { useTheme } from '@/contexts/ThemeContext'
import { ArrowLeft, AlertTriangle, CheckCircle } from 'lucide-react'

export default function WeightBeltPage() {
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  return (
    <div className={`min-h-screen py-4 xxs:py-3 px-3 xxs:px-2 ${
      isDark ? 'bg-gray-900' : 'bg-gradient-to-b from-green-50 to-white'
    }`}>
      <div className="max-w-3xl mx-auto">
        <Link
          href="/equipment"
          className={`inline-flex items-center gap-1 mb-4 xxs:mb-3 text-sm xxs:text-xs ${
            isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
          }`}
        >
          <ArrowLeft className="w-4 h-4 xxs:w-3.5 xxs:h-3.5" />
          장비 가이드 목록
        </Link>

        <article className={`rounded-xl shadow-lg overflow-hidden ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}>
          {/* 헤더 */}
          <div className={`p-6 xxs:p-4 ${isDark ? 'bg-green-900/30' : 'bg-green-50'}`}>
            <h1 className={`text-2xl xxs:text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              웨이트벨트 사용법
            </h1>
            <p className={`mt-2 text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              적절한 웨이트 조절로 효율적이고 안전한 다이빙을 하세요.
            </p>
          </div>

          <div className="p-6 xxs:p-4 space-y-6 xxs:space-y-4">
            {/* 웨이트벨트가 필요한 이유 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-3 xxs:mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                웨이트벨트가 필요한 이유
              </h2>
              <div className="space-y-2 xxs:space-y-1.5">
                {[
                  { emoji: '⚖️', title: '부력 조절', desc: '웻슈트의 부력을 상쇄' },
                  { emoji: '⬇️', title: '하강 편의', desc: '에너지 절약하며 하강' },
                  { emoji: '🎯', title: '자세 안정', desc: '수중에서 안정된 자세 유지' },
                  { emoji: '💪', title: '효율성', desc: '킥 횟수 감소로 산소 절약' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-lg">{item.emoji}</span>
                    <span className={`font-semibold text-sm xxs:text-xs ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {item.title}
                    </span>
                    <span className={`text-sm xxs:text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      : {item.desc}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* 적정 웨이트란? */}
            <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
              <h3 className={`font-bold text-base xxs:text-sm mb-2 ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                적정 웨이트란?
              </h3>
              <p className={`text-sm xxs:text-xs ${isDark ? 'text-blue-200/80' : 'text-blue-700'}`}>
                수면에서 풀 브레스 시 눈높이까지 잠기고,
                숨을 내쉬면 천천히 가라앉는 정도가 적절합니다.
              </p>
            </div>

            {/* 웨이트 계산법 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                웨이트 계산법
              </h2>
              <div className="space-y-3 xxs:space-y-2">
                <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                  <h3 className={`font-bold text-sm xxs:text-xs mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    기본 공식
                  </h3>
                  <div className={`p-3 xxs:p-2 rounded-lg text-center ${isDark ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                    <p className={`text-base xxs:text-sm font-bold ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                      체중(kg) × 슈트 두께 계수
                    </p>
                  </div>
                  <div className={`mt-3 text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    <p className="font-semibold mb-1">슈트 두께 계수:</p>
                    <ul className="space-y-1 ml-2">
                      <li>• 1-2mm: 0.5-1%</li>
                      <li>• 3mm: 2-3%</li>
                      <li>• 5mm: 4-5%</li>
                      <li>• 7mm: 6-8%</li>
                    </ul>
                  </div>
                </div>

                <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-green-900/20' : 'bg-green-50'}`}>
                  <h3 className={`font-bold text-sm xxs:text-xs mb-2 ${isDark ? 'text-green-300' : 'text-green-800'}`}>
                    예시 계산
                  </h3>
                  <div className={`space-y-2 xxs:space-y-1 text-sm xxs:text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    <div>
                      <p><strong>Case 1:</strong> 체중 70kg, 3mm 슈트</p>
                      <p className="ml-4">→ 70 × 0.03 = 2.1kg ≈ 2kg</p>
                    </div>
                    <div>
                      <p><strong>Case 2:</strong> 체중 60kg, 5mm 슈트</p>
                      <p className="ml-4">→ 60 × 0.05 = 3kg</p>
                    </div>
                    <div>
                      <p><strong>Case 3:</strong> 체중 80kg, 3mm 슈트</p>
                      <p className="ml-4">→ 80 × 0.03 = 2.4kg ≈ 2.5kg</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 주의사항 */}
            <div className={`p-4 xxs:p-3 rounded-xl border-l-4 border-yellow-500 ${isDark ? 'bg-yellow-900/20' : 'bg-yellow-50'}`}>
              <div className="flex items-start gap-2">
                <AlertTriangle className={`w-5 h-5 xxs:w-4 xxs:h-4 flex-shrink-0 ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`} />
                <div>
                  <h3 className={`font-bold text-base xxs:text-sm mb-2 ${isDark ? 'text-yellow-300' : 'text-yellow-800'}`}>
                    주의사항
                  </h3>
                  <ul className={`space-y-1 text-sm xxs:text-xs ${isDark ? 'text-yellow-200/80' : 'text-yellow-700'}`}>
                    <li>• 계산은 참고용, 반드시 실제 테스트 필요</li>
                    <li>• 개인차가 크므로 0.5kg 단위로 조정</li>
                    <li>• 민물과 바닷물의 부력 차이 고려</li>
                    <li>• 웨이트 과다는 위험합니다</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 웨이트 조절 팁 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                웨이트 조절 팁
              </h2>
              <div className="space-y-2 xxs:space-y-1.5">
                {[
                  {
                    title: '초보자',
                    items: ['조금 적게 시작 (계산값 - 0.5kg)', '하강이 힘들면 0.5kg씩 추가', '웨이트 과다보다 부족이 안전']
                  },
                  {
                    title: '중급자',
                    items: ['수심별로 다르게 조절', '10m: 중성 부력', '20m+: 약간의 음성 부력', '상승 시 킥 최소화가 목표']
                  },
                  {
                    title: '고급자',
                    items: ['최소 웨이트로 효율 극대화', '프리폴 구간 활용', '웨이트 위치 최적화', '넥 웨이트 고려']
                  },
                ].map((section, idx) => (
                  <div key={idx} className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                    <h3 className={`font-bold text-sm xxs:text-xs mb-2 ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                      {section.title}
                    </h3>
                    <ul className={`space-y-1 text-xs xxs:text-[10px] ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      {section.items.map((item, i) => (
                        <li key={i}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* 웨이트 배치 */}
            <div>
              <h2 className={`text-xl xxs:text-lg font-bold mb-4 xxs:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                웨이트 배치
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 xxs:gap-3">
                <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                  <h3 className={`font-bold text-sm xxs:text-xs mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    허리 벨트
                  </h3>
                  <ul className={`space-y-1 text-xs xxs:text-[10px] ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    <li>• 가장 일반적</li>
                    <li>• 배 쪽이 아닌 허리 뒤에</li>
                    <li>• 너무 조이지 않게</li>
                    <li>• 퀵 릴리스 필수</li>
                  </ul>
                </div>
                <div className={`p-4 xxs:p-3 rounded-xl ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                  <h3 className={`font-bold text-sm xxs:text-xs mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    넥 웨이트
                  </h3>
                  <ul className={`space-y-1 text-xs xxs:text-[10px] ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                    <li>• 고급 기술</li>
                    <li>• 수평 자세 유지</li>
                    <li>• 1-2kg 정도</li>
                    <li>• 전문가 조언 필요</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 안전 수칙 */}
            <div className={`p-4 xxs:p-3 rounded-xl border-l-4 border-red-500 ${isDark ? 'bg-red-900/20' : 'bg-red-50'}`}>
              <div className="flex items-start gap-2">
                <AlertTriangle className={`w-5 h-5 xxs:w-4 xxs:h-4 flex-shrink-0 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                <div>
                  <h3 className={`font-bold text-base xxs:text-sm mb-2 ${isDark ? 'text-red-300' : 'text-red-900'}`}>
                    안전 수칙
                  </h3>
                  <ul className={`space-y-1 text-sm xxs:text-xs ${isDark ? 'text-red-200' : 'text-red-800'}`}>
                    <li>• 웨이트벨트는 반드시 퀵 릴리스 사용</li>
                    <li>• 긴급 상황 시 즉시 버릴 수 있어야 함</li>
                    <li>• 과도한 웨이트는 상승 시 위험</li>
                    <li>• 새 슈트는 웨이트 재조정 필요</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  )
}
